import { supabase } from '../lib/supabase.js';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import DateSynchronizationService from './DateSynchronizationService.js';

/**
 * Timeline Service
 * Handles all operations related to timeline items, week focus, and milestone tracking
 */

class TimelineService {
  // ============= Timeline Items =============
  
  /**
   * Normalize priority values to ensure database constraint compliance
   */
  static normalizePriority(priority) {
    if (!priority) return 'medium';
    const validPriorities = ['critical', 'high', 'medium', 'low'];
    if (validPriorities.includes(priority)) return priority;
    
    const mapping = {
      'urgent': 'critical',
      'normal': 'medium',
      'important': 'high'
    };
    
    return mapping[priority] || 'medium';
  }

  /**
   * Normalize source_type values to ensure database constraint compliance
   */
  static normalizeSourceType(sourceType) {
    if (!sourceType) return 'custom';
    
    // Valid source types from actual database constraint
    const validSourceTypes = [
      'milestone', 'custom', 'manual', 'college_deadline', 'milestone_template', 
      'milestone_subtask', 'profile_generation', 'essay_generation', 'fafsa', 
      'css_profile', 'strategic_milestone', 'system_generated', 'user_created', 
      'deadline', 'task', 'event', 'reminder'
    ];
    
    // If it's already valid, return as-is
    if (validSourceTypes.includes(sourceType)) return sourceType;
    
    // Mapping for common invalid values
    const mapping = {
      'action': 'task',                // Next actions are tasks
      'suggestion': 'system_generated', // AI suggestions are system generated
      'preparation': 'task',           // Preparation tasks are tasks
      'strategic': 'strategic_milestone', // Strategic items are strategic milestones
      'message': 'user_created',       // Message-based tasks are user created
      'calendar': 'event',             // Calendar items are events
      'shopping': 'user_created',      // Shopping items are user created
      'family_activity': 'event'       // Family activities are events
    };
    
    return mapping[sourceType] || 'custom';
  }
  
  /**
   * Calculate hard_deadline for a task based on category and scheduled_date
   * Returns null for categories that don't require hard deadlines (like profile)
   */
  calculateHardDeadline(scheduledDate, category) {
    if (!scheduledDate) return null;

    const baseDate = typeof scheduledDate === 'string' ? parseISO(scheduledDate) : scheduledDate;

    // Category-based hard deadline buffers (in days)
    const deadlineBuffers = {
      'application': 1,      // Applications: hard deadline = scheduled + 1 day
      'essay': 7,            // Essays: hard deadline = scheduled + 7 days
      'financial_aid': 3,    // Financial aid: hard deadline = scheduled + 3 days
      'testing': 14,         // Testing: hard deadline = scheduled + 14 days
      'research': 21,        // Research: hard deadline = scheduled + 21 days (more flexible)
      'profile': null        // Profile: no hard deadline (can be completed anytime)
    };

    const buffer = deadlineBuffers[category];
    if (buffer === null) return null;

    // Default buffer for unknown categories
    const daysToAdd = buffer !== undefined ? buffer : 7;

    const hardDeadline = new Date(baseDate);
    hardDeadline.setDate(hardDeadline.getDate() + daysToAdd);

    return format(hardDeadline, 'yyyy-MM-dd');
  }

  /**
   * Create or update a timeline item
   */
  async upsertTimelineItem(item) {
    try {
      let result;

      // Check if this is a real database ID (UUID) or a temporary ID
      const isValidUUID = (id) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return id && uuidRegex.test(id);
      };

      // If item has a valid database ID, it's an update
      if (item.id && isValidUUID(item.id)) {
        console.log('Updating existing timeline item with ID:', item.id);

        // Handle date synchronization - ensure both due_date and scheduled_date are set
        let syncedDates = {};
        if (item.due_date && !item.scheduled_date) {
          syncedDates.scheduled_date = item.due_date;
        } else if (item.scheduled_date && !item.due_date) {
          // Keep scheduled_date as is, don't auto-set due_date on updates
          syncedDates = {};
        }

        const normalizedItem = {
          ...item,
          ...syncedDates,
          priority: TimelineService.normalizePriority(item.priority),
          source_type: TimelineService.normalizeSourceType(item.source_type),
          updated_at: new Date().toISOString()
        };
        const { data, error } = await supabase
          .from('timeline_items')
          .update(normalizedItem)
          .eq('id', item.id)
          .select()
          .single();

        result = { data, error };
      } else {
        // For new items, use insert to avoid constraint issues
        // Remove any id field (could be temporary like 'milestone-0')
        const { id, ...itemWithoutId } = item;

        console.log('Inserting new timeline item, removing temp ID:', id);

        // Handle date synchronization for new items - ensure both due_date and scheduled_date are set
        let syncedDates = {};
        if (itemWithoutId.due_date && !itemWithoutId.scheduled_date) {
          syncedDates.scheduled_date = itemWithoutId.due_date;
        } else if (itemWithoutId.scheduled_date && !itemWithoutId.due_date) {
          syncedDates.due_date = itemWithoutId.scheduled_date;
        }

        // Calculate hard_deadline for new tasks if not provided
        let hardDeadline = itemWithoutId.hard_deadline;
        if (!hardDeadline && (syncedDates.scheduled_date || itemWithoutId.scheduled_date) && itemWithoutId.category) {
          hardDeadline = this.calculateHardDeadline(
            syncedDates.scheduled_date || itemWithoutId.scheduled_date,
            itemWithoutId.category
          );
        }

        const normalizedItemWithoutId = {
          ...itemWithoutId,
          ...syncedDates,
          hard_deadline: hardDeadline,
          priority: TimelineService.normalizePriority(itemWithoutId.priority),
          source_type: TimelineService.normalizeSourceType(itemWithoutId.source_type),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        console.log('Item to insert (normalized):', normalizedItemWithoutId);

        const { data, error } = await supabase
          .from('timeline_items')
          .insert(normalizedItemWithoutId)
          .select()
          .single();

        result = { data, error };
      }

      if (result.error) throw result.error;

      console.log('Timeline item saved successfully:', result.data);
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Error saving timeline item:', error);
      console.error('Item data:', item);
      return { data: null, error };
    }
  }

  /**
   * Get timeline items for a user within a date range
   */
  async getTimelineItems(userId, startDate, endDate) {
    try {
      let query = supabase
        .from('timeline_items')
        .select(`
          *,
          college:college_journey_colleges(
            id,
            name,
            category
          )
        `)
        .eq('user_id', userId)
        .order('scheduled_date', { ascending: true })

      if (startDate) {
        query = query.gte('scheduled_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        query = query.lte('scheduled_date', format(endDate, 'yyyy-MM-dd'));
      }

      const { data, error } = await query;
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching timeline items:', error);
      return { data: [], error };
    }
  }

  /**
   * Move a timeline item to a new date/time
   * Validates against hard_deadline to prevent rescheduling past fixed deadlines
   */
  async moveTimelineItem(itemId, newDate, newTime = null) {
    try {
      // Fetch task to check hard_deadline
      const { data: task, error: fetchError } = await supabase
        .from('timeline_items')
        .select('hard_deadline, title, category')
        .eq('id', itemId)
        .single();

      if (fetchError) throw fetchError;

      // Validate against hard_deadline
      if (task.hard_deadline) {
        const hardDeadlineDate = parseISO(task.hard_deadline);
        const newScheduledDate = parseISO(format(newDate, 'yyyy-MM-dd'));

        if (newScheduledDate > hardDeadlineDate) {
          throw new Error(
            `Cannot reschedule "${task.title}" past its hard deadline of ${format(hardDeadlineDate, 'MMM dd, yyyy')}. ` +
            `This deadline is fixed and cannot be extended.`
          );
        }
      }

      const updateData = {
        scheduled_date: format(newDate, 'yyyy-MM-dd'),
        status: 'rescheduled',
        updated_at: new Date().toISOString()
      };

      if (newTime) {
        updateData.scheduled_time = newTime;
      }

      const { data, error } = await supabase
        .from('timeline_items')
        .update(updateData)
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error moving timeline item:', error);
      return { data: null, error };
    }
  }

  /**
   * Clean up duplicate timeline items for a user
   * Keeps the most recently created item when duplicates are found
   */
  async cleanupDuplicateTimelineItems(userId) {
    try {
      console.log('Starting timeline cleanup for user:', userId);
      
      // Get all timeline items for the user
      const { data: allItems, error: fetchError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      if (!allItems || allItems.length === 0) {
        console.log('No timeline items found for cleanup');
        return { data: { removed: 0 }, error: null };
      }
      
      // Group items by title (normalized) to find duplicates
      const itemsByTitle = {};
      allItems.forEach(item => {
        // Normalize title for comparison (lowercase, trim)
        const normalizedTitle = (item.title || '').toLowerCase().trim();
        if (normalizedTitle) {
          if (!itemsByTitle[normalizedTitle]) {
            itemsByTitle[normalizedTitle] = [];
          }
          itemsByTitle[normalizedTitle].push(item);
        }
      });
      
      // Find and remove duplicates
      const idsToRemove = [];
      Object.entries(itemsByTitle).forEach(([title, items]) => {
        if (items.length > 1) {
          console.log(`Found ${items.length} duplicates for "${title}"`);
          // Keep the first one (most recent due to ordering), remove the rest
          for (let i = 1; i < items.length; i++) {
            idsToRemove.push(items[i].id);
            console.log(`  - Will remove duplicate: ${items[i].id} (created: ${items[i].created_at})`);
          }
        }
      });
      
      // Remove duplicates
      if (idsToRemove.length > 0) {
        console.log(`Removing ${idsToRemove.length} duplicate timeline items`);
        const { error: deleteError } = await supabase
          .from('timeline_items')
          .delete()
          .in('id', idsToRemove);
        
        if (deleteError) throw deleteError;
        
        console.log(`Successfully removed ${idsToRemove.length} duplicate items`);
        return { data: { removed: idsToRemove.length, removedIds: idsToRemove }, error: null };
      } else {
        console.log('No duplicates found to remove');
        return { data: { removed: 0 }, error: null };
      }
    } catch (error) {
      console.error('Error cleaning up timeline duplicates:', error);
      return { data: null, error };
    }
  }

  /**
   * Get tasks for a specific date
   */
  async getTasksByDate(userId, date) {
    try {
      const targetDate = format(date, 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('scheduled_date', targetDate)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching tasks by date:', error);
      return { data: [], error };
    }
  }

  /**
   * Update timeline item status
   * When status is 'completed', delegates to completeTask() for MVV tracking
   */
  async updateTimelineItemStatus(itemId, status, notes = null) {
    try {
      // If completing a task, use completeTask() to get MVV tracking
      if (status === 'completed') {
        return await this.completeTask(itemId, notes);
      }

      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updateData.notes = notes;
      }

      const { data, error } = await supabase
        .from('timeline_items')
        .update(updateData)
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating timeline item status:', error);
      return { data: null, error };
    }
  }

  /**
   * Start a task (change status to 'active')
   */
  async startTask(taskId) {
    try {
      const { data, error } = await supabase
        .from('timeline_items')
        .update({
          status: 'active',
          started_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error starting task:', error);
      return { data: null, error };
    }
  }

  /**
   * Complete a task (change status to 'completed')
   * Automatically detects and tracks MVV completions
   */
  async completeTask(taskId, notes = null) {
    try {
      // First, fetch the task to check for MVV fields
      const { data: task, error: fetchError } = await supabase
        .from('timeline_items')
        .select('started_at, mvv_time_required, optimal_time_required, mvv_risk')
        .eq('id', taskId)
        .single();

      if (fetchError) throw fetchError;

      const updateData = {
        status: 'completed',
        completed_at: new Date().toISOString(),
        completion_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updateData.notes = notes;
      }

      // MVV Completion Detection Logic
      // Detect if this task was completed using MVV approach based on time spent
      if (task?.mvv_time_required && task?.optimal_time_required) {
        let timeSpentMinutes = null;

        // Calculate time spent if started_at is available
        if (task.started_at) {
          const startTime = new Date(task.started_at);
          const endTime = new Date();
          timeSpentMinutes = Math.round((endTime - startTime) / (1000 * 60));
        }

        // Mark as MVV completion if:
        // 1. Time spent is available and ≤ MVV time * 1.2 (20% buffer)
        // 2. OR time spent is significantly closer to MVV than optimal (within 50% threshold)
        const mvvThreshold = task.mvv_time_required * 1.2;
        const optimalThreshold = task.optimal_time_required * 0.5; // Halfway point

        let completedAsMVV = false;

        if (timeSpentMinutes !== null) {
          // If we have time tracking, use it
          completedAsMVV = timeSpentMinutes <= mvvThreshold;
        } else if (task.mvv_risk && task.mvv_risk >= 0.3) {
          // If no time tracking but task has significant MVV risk,
          // assume it might be MVV (user can manually enhance later)
          // Only flag high-risk MVV tasks (risk >= 0.3) as potential enhancements
          completedAsMVV = true;
        }

        if (completedAsMVV) {
          updateData.completed_as_mvv = true;
          updateData.MVV_completion_date = new Date().toISOString();
          console.log(`✓ Task ${taskId} detected as MVV completion (time: ${timeSpentMinutes}min, threshold: ${mvvThreshold}min)`);
        }
      }

      const { data, error } = await supabase
        .from('timeline_items')
        .update(updateData)
        .eq('id', taskId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error completing task:', error);
      return { data: null, error };
    }
  }

  /**
   * Skip a task (change status to 'skipped')
   */
  async skipTask(taskId, reason = null) {
    try {
      const updateData = {
        status: 'skipped',
        updated_at: new Date().toISOString()
      };

      if (reason) {
        updateData.notes = `Skipped: ${reason}`;
      }

      const { data, error } = await supabase
        .from('timeline_items')
        .update(updateData)
        .eq('id', taskId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error skipping task:', error);
      return { data: null, error };
    }
  }

  /**
   * Get smart navigation URL for a task
   */
  getTaskActionUrl(task) {
    // Priority: module_link > resource_urls[0] > default fallback
    if (task.module_link) {
      return task.module_link;
    }
    
    if (task.resource_urls && Array.isArray(task.resource_urls) && task.resource_urls.length > 0) {
      return task.resource_urls[0];
    }
    
    // Fallback based on task type/category
    if (task.type === 'college_research' || task.category === 'college') {
      return '/college/colleges';
    } else if (task.type === 'essay' || task.category === 'essay') {
      return '/college/essay-hub';
    } else if (task.type === 'profile' || task.profile_section) {
      return '/college/profile';
    } else if (task.type === 'assessment' || task.category === 'assessment') {
      return '/assessment';
    }
    
    // Default fallback
    return '/college/timeline';
  }

  /**
   * Check if a task has an available module or tool to work on
   */
  hasModuleLink(task) {
    return !!(task.module_link || 
             (task.resource_urls && Array.isArray(task.resource_urls) && task.resource_urls.length > 0));
  }

  /**
   * Get appropriate icon for task action based on module type
   */
  getTaskActionIcon(task) {
    if (task.module_link) {
      return '▶︎'; // Generic tool icon
    }
    
    if (task.resource_urls && task.resource_urls.length > 0) {
      return '🔗'; // Link icon for external resources
    }
    
    return null; // No special icon
  }

  /**
   * Get action button text based on task and module type
   */
  getTaskActionText(task) {
    if (task.module_link) {
      // Customize text based on module
      if (task.module_link.includes('/essays')) return 'Work on Essay';
      if (task.module_link.includes('/colleges')) return 'Research College';
      if (task.module_link.includes('/profile')) return 'Update Profile';
      if (task.module_link.includes('/assessment')) return 'Take Assessment';
      return 'Work on This Task';
    }
    
    if (task.resource_urls && task.resource_urls.length > 0) {
      return 'View Resources';
    }
    
    return 'View Task';
  }

  /**
   * Navigate to task module/tool (handles both internal and external links)
   */
  navigateToTask(task, navigate) {
    const url = this.getTaskActionUrl(task);
    
    // Check if it's an external URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Internal route - use React Router
      navigate(url);
    }
  }

  /**
   * Delete a timeline item
   */
  async deleteTimelineItem(itemId) {
    try {
      const { error } = await supabase
        .from('timeline_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting timeline item:', error);
      return { error };
    }
  }

  /**
   * Batch create timeline items from milestones
   */
  async createTimelineItemsFromMilestones(userId, milestones) {
    try {
      const timelineItems = milestones.map(milestone => {
        const targetDate = milestone.targetDate ? format(parseISO(milestone.targetDate), 'yyyy-MM-dd') : null;
        return {
          user_id: userId,
          source_type: 'milestone',
          source_id: milestone.id,
          title: milestone.title,
          description: milestone.description || milestone.personalizedMessage,
          due_date: targetDate,
          scheduled_date: targetDate,
          estimated_duration: milestone.estimatedTime ? this.parseEstimatedTime(milestone.estimatedTime) : 60,
          category: milestone.category,
          priority: milestone.urgency || 'medium',
          status: 'planned',
          tags: milestone.tags || [],
          dependencies: milestone.dependencies ? { items: milestone.dependencies } : null,
          epic_id: this.getEpicIdForMilestone(milestone.id), // Auto-assign epic_id
          workflow_phase: 'ready' // Default to ready phase
        };
      });

      const { data, error } = await supabase
        .from('timeline_items')
        .insert(timelineItems)
        .select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating timeline items from milestones:', error);
      return { data: [], error };
    }
  }

  // ============= Week Focus Items =============

  /**
   * Get week focus items for a specific week
   */
  async getWeekFocusItems(userId, weekStart = null) {
    try {
      const start = weekStart || startOfWeek(new Date(), { weekStartsOn: 1 }); // Monday start
      const weekStartDate = format(start, 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('week_focus_items')
        .select(`
          *,
          timeline_item:timeline_items(
            id,
            title,
            description,
            category,
            priority,
            status,
            estimated_duration,
            college:college_journey_colleges(
              id,
              name
            )
          )
        `)
        .eq('user_id', userId)
        .eq('week_start', weekStartDate)
        .order('day_of_week', { ascending: true })
        .order('order_index', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching week focus items:', error);
      return { data: [], error };
    }
  }

  /**
   * Add item to week focus
   */
  async addToWeekFocus(userId, timelineItemId, dayOfWeek, weekStart = null) {
    try {
      const start = weekStart || startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekStartDate = format(start, 'yyyy-MM-dd');

      // Get the max order index for this day
      const { data: existing } = await supabase
        .from('week_focus_items')
        .select('order_index')
        .eq('user_id', userId)
        .eq('week_start', weekStartDate)
        .eq('day_of_week', dayOfWeek)
        .order('order_index', { ascending: false })
        .limit(1);

      const orderIndex = existing && existing.length > 0 ? existing[0].order_index + 1 : 0;

      const { data, error } = await supabase
        .from('week_focus_items')
        .insert({
          user_id: userId,
          week_start: weekStartDate,
          timeline_item_id: timelineItemId,
          day_of_week: dayOfWeek,
          order_index: orderIndex
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error adding to week focus:', error);
      return { data: null, error };
    }
  }

  /**
   * Add quick task to week focus (doesn't require timeline item)
   */
  async addQuickTaskToWeekFocus(userId, title, dayOfWeek, weekStart = null) {
    try {
      const start = weekStart || startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekStartDate = format(start, 'yyyy-MM-dd');

      // Get the max order index for this day
      const { data: existing } = await supabase
        .from('week_focus_items')
        .select('order_index')
        .eq('user_id', userId)
        .eq('week_start', weekStartDate)
        .eq('day_of_week', dayOfWeek)
        .order('order_index', { ascending: false })
        .limit(1);

      const orderIndex = existing && existing.length > 0 ? existing[0].order_index + 1 : 0;

      const { data, error } = await supabase
        .from('week_focus_items')
        .insert({
          user_id: userId,
          week_start: weekStartDate,
          quick_task_title: title,
          day_of_week: dayOfWeek,
          order_index: orderIndex
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error adding quick task to week focus:', error);
      return { data: null, error };
    }
  }

  /**
   * Reorder week focus items
   */
  async reorderWeekFocusItems(userId, dayOfWeek, itemIds, weekStart = null) {
    try {
      const start = weekStart || startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekStartDate = format(start, 'yyyy-MM-dd');

      const updates = itemIds.map((id, index) => ({
        id,
        order_index: index
      }));

      const { error } = await supabase.rpc('batch_update_week_focus_order', {
        p_user_id: userId,
        p_week_start: weekStartDate,
        p_day_of_week: dayOfWeek,
        p_updates: updates
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error reordering week focus items:', error);
      return { error };
    }
  }

  /**
   * Remove from week focus
   */
  async removeFromWeekFocus(itemId) {
    try {
      const { error } = await supabase
        .from('week_focus_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing from week focus:', error);
      return { error };
    }
  }

  // ============= Milestone Completions =============

  /**
   * Track milestone completion
   */
  async trackMilestoneCompletion(userId, milestoneId, category, notes = null) {
    try {
      const { data, error } = await supabase
        .from('milestone_completions')
        .upsert({
          user_id: userId,
          milestone_id: milestoneId,
          milestone_category: category,
          notes,
          completed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error tracking milestone completion:', error);
      return { data: null, error };
    }
  }

  /**
   * Get completed milestones for a user
   */
  async getCompletedMilestones(userId) {
    try {
      const { data, error } = await supabase
        .from('milestone_completions')
        .select('*')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching completed milestones:', error);
      return { data: [], error };
    }
  }

  /**
   * Remove milestone completion
   */
  async removeMilestoneCompletion(userId, milestoneId) {
    try {
      const { error } = await supabase
        .from('milestone_completions')
        .delete()
        .eq('user_id', userId)
        .eq('milestone_id', milestoneId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing milestone completion:', error);
      return { error };
    }
  }

  // ============= Timeline Preferences =============

  /**
   * Get or create user timeline preferences
   */
  async getUserPreferences(userId) {
    try {
      let { data, error } = await supabase
        .from('timeline_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      // If no preferences exist, create defaults
      if (!data) {
        const defaultPreferences = {
          user_id: userId,
          preferred_study_times: [
            { start_time: '14:00', end_time: '18:00', days: [1, 2, 3, 4, 5] }
          ],
          max_daily_hours: 4,
          break_duration: 15,
          auto_schedule_enabled: true,
          smart_suggestions_enabled: true,
          workload_alerts_enabled: true,
          default_view: 'week',
          show_completed_tasks: true,
          color_scheme: 'category'
        };

        const { data: newData, error: insertError } = await supabase
          .from('timeline_preferences')
          .insert(defaultPreferences)
          .select()
          .single();

        if (insertError) throw insertError;
        data = newData;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user timeline preferences
   */
  async updateUserPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        .from('timeline_preferences')
        .update({
          ...preferences,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating user preferences:', error);
      return { data: null, error };
    }
  }

  // ============= Helper Functions =============

  /**
   * Parse estimated time string to minutes
   */
  /**
   * Map milestone ID to Epic ID based on milestone categories
   */
  getEpicIdForMilestone(milestoneId) {
    // Updated to match the epic IDs used in Epic dashboard components
    const epicMappings = {
      // Profile & Foundation Epic
      'strategic_profile': 'profile',
      'academic_profile': 'profile',
      'activities_list': 'profile',
      
      // College Research Epic
      'college_research': 'research',
      'college_research_reach': 'research',
      'college_research_target': 'research', 
      'college_research_safety': 'research',
      'reach_schools': 'research',
      'target_schools': 'research',
      'safety_schools': 'research',
      'campus_visits': 'research',
      'college_visits': 'research',
      
      // Essays Epic
      'personal_statement': 'essay',
      'supplemental_essays': 'essay',
      'common_app_essay': 'essay',
      
      // Applications Epic (keep same)
      'common_app': 'applications',
      'recommendation_letters': 'applications',
      'early_applications': 'applications',
      'regular_applications': 'applications',
      
      // Testing Epic
      'test_scores': 'testing',
      'sat_prep': 'testing',
      'act_prep': 'testing',
      'send_test_scores': 'testing',
      
      // Financial Aid Epic
      'complete_fafsa': 'financial',
      'complete_css': 'financial',
      'financial_aid_docs': 'financial'
    };
    
    // Check direct mapping
    if (epicMappings[milestoneId]) {
      return epicMappings[milestoneId];
    }
    
    // Try to infer from milestone ID patterns using updated epic IDs
    if (milestoneId.includes('test') || milestoneId.includes('sat') || milestoneId.includes('act')) {
      return 'testing';
    }
    if (milestoneId.includes('college') || milestoneId.includes('school') || milestoneId.includes('visit')) {
      return 'research';
    }
    if (milestoneId.includes('essay') || milestoneId.includes('statement')) {
      return 'essay';
    }
    if (milestoneId.includes('fafsa') || milestoneId.includes('css') || milestoneId.includes('financial')) {
      return 'financial';
    }
    if (milestoneId.includes('submit') || milestoneId.includes('application')) {
      return 'applications';
    }
    if (milestoneId.includes('profile') || milestoneId.includes('activities') || milestoneId.includes('strategic')) {
      return 'profile';
    }
    
    // Default to profile for unknown milestones
    return 'profile';
  }

  parseEstimatedTime(timeStr) {
    if (!timeStr) return 60; // Default 1 hour

    const lowerStr = timeStr.toLowerCase();
    let minutes = 60; // Default

    if (lowerStr.includes('min')) {
      const match = lowerStr.match(/(\d+)\s*min/);
      if (match) minutes = parseInt(match[1]);
    } else if (lowerStr.includes('hour') || lowerStr.includes('hr')) {
      const match = lowerStr.match(/(\d+(?:\.\d+)?)\s*(hour|hr)/);
      if (match) minutes = Math.round(parseFloat(match[1]) * 60);
    }

    return minutes;
  }

  /**
   * Get smart suggestions for scheduling
   */
  async getSmartSuggestions(userId, startDate, endDate) {
    try {
      // Get user preferences
      const { data: prefs } = await this.getUserPreferences(userId);
      
      // Get existing timeline items
      const { data: existingItems } = await this.getTimelineItems(userId, startDate, endDate);
      
      // Get uncompleted milestones that aren't scheduled
      const { data: milestones } = await supabase
        .from('milestone_completions')
        .select('milestone_id')
        .eq('user_id', userId);
      
      const completedIds = milestones?.map(m => m.milestone_id) || [];
      
      // Algorithm to suggest optimal scheduling
      const suggestions = this.generateSchedulingSuggestions(
        existingItems,
        completedIds,
        prefs,
        startDate,
        endDate
      );

      return { data: suggestions, error: null };
    } catch (error) {
      console.error('Error getting smart suggestions:', error);
      return { data: [], error };
    }
  }

  /**
   * Generate scheduling suggestions based on workload and preferences
   */
  generateSchedulingSuggestions(existingItems, completedIds, preferences, startDate, endDate) {
    // This would implement the smart scheduling algorithm
    // For now, return placeholder
    return [];
  }
}

// Export singleton instance
export default new TimelineService();